'use client';

import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn } from '@ahm/common-helpers';
import React, { useMemo } from 'react';

import { useGetFoodOrderCategories } from '@/api/ahafood/get-category-fn';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

type SolomealCategoryProps = {
  selectedCategory?: string;
  onCategoryChange?: (category: string | undefined) => void;
};

interface CategoryItem {
  id: string | number;
  slug: string;
  title_vi_vn: string;
  icon_img_url?: string;
}

export default function SolomealCategory({
  selectedCategory,
  onCategoryChange,
}: SolomealCategoryProps) {
  const { user } = useAuthStore();

  const { data: categoriesData, isLoading } = useGetFoodOrderCategories({
    type: 'SOLO_MEAL',
    limit: 20,
  });

  const allCategories = useMemo<CategoryItem[] | null>(() => {
    if (!categoriesData?.categories) return null;
    const allCategory: CategoryItem = {
      id: 'all',
      slug: '',
      title_vi_vn: 'Tất cả',
      icon_img_url: '/static/icons/solomeal-category.svg',
    };
    return [allCategory, ...categoriesData.categories];
  }, [categoriesData?.categories]);

  const handleClick = (categorySlug: string) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'solomeal_category_toggle',
      selected_category: categorySlug,
      user_id: user?.cunstomerId,
    });
    const newCategory =
      selectedCategory === categorySlug ? undefined : categorySlug;
    onCategoryChange?.(newCategory);
  };

  if (isLoading || !categoriesData?.categories) {
    return (
      <div className="bg-white py-3">
        <div className="flex gap-3 overflow-x-hidden px-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-2 shrink-0 w-16 lg:w-20"
            >
              <Skeleton className="aspect-square w-full rounded-full" />
              <Skeleton className="h-4 w-12 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!allCategories || allCategories.length === 0) return null;

  return (
    <div className="bg-white py-3">
      <FreeDragCarousel contentClassName="ml-0">
        {allCategories.map((category, idx) => {
          const isSelected =
            selectedCategory === category.slug ||
            (selectedCategory === undefined && category.slug === '');

          return (
            <FreeDragCarousel.Item
              key={category.id}
              className={cn(
                'basis-auto shrink-0',
                idx === 0 ? 'pl-4' : 'pl-3',
                idx === allCategories.length - 1 && 'pr-4'
              )}
            >
              <button
                onClick={() => handleClick(category.slug)}
                className="flex flex-col items-center gap-2 group bg-transparent border-none p-0"
                data-category={category.slug}
              >
                <div className="relative aspect-square w-16 lg:w-20 flex-none overflow-hidden rounded-full transition-all hover:scale-105">
                  {category.slug === '' ? (
                    <img
                      src="/static/icons/solomeal-category.svg"
                      alt="Tất cả"
                      className="size-full object-cover rounded-full"
                    />
                  ) : (
                    <ImageWithFallback
                      src={
                        category.icon_img_url ??
                        '/static/images/empty-category.webp'
                      }
                      alt={category.slug}
                      fill
                      sizes="80px"
                      style={{ objectFit: 'cover' }}
                      className="scale-110"
                      fallbackSrc="/static/images/empty-category.webp"
                    />
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs lg:text-sm text-center leading-tight max-w-full line-clamp-2 font-medium transition-colors',
                    isSelected
                      ? 'text-primary font-bold'
                      : 'text-neutral-70 group-hover:text-primary-50'
                  )}
                >
                  {category.title_vi_vn}
                </span>
                {/* Active indicator — small dot under text (thay cho ring border) */}
                {isSelected ? (
                  <span className="size-1 rounded-full bg-primary" />
                ) : null}
              </button>
            </FreeDragCarousel.Item>
          );
        })}
      </FreeDragCarousel>
    </div>
  );
}
